import React, { useEffect, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Payment, { paymentSchema } from '../../Booking/components/Payment'
import { useDispatch, useSelector } from 'react-redux'
import ReservationFields from './ReservationFields'
import DriverFields from './DriverFields'
import MilesNCash from '../../Booking/components/MilesNCash'
import {
  setMilesNBonusAccount,
  setSelectedVehicle,
  resetMBData,
  resetMilesNBonusAccount,
  setModifiedPrices,
  setNeedsChanges,
} from '../../../../js/lib/redux/slices/booking'
import Terms from '../../Terms'
import constants from '../../../../js/lib/constants'
import { postData, getData } from '../../../../js/helpers/apiHelper'
import MilesNCashOTPModal from '../../Booking/components/MilesNCashOTPModal'
import * as lib from '../../../../js/lib/lib'
import Alert from '../../Base/Alert'
import { CircularProgress } from '@mui/material'
import SummaryMobilePanel from '../../Booking/components/SummaryMobilePanel'
import $ from 'jquery'
import showMilesNBonus from '../../../../js/helpers/showMilesNBonus'

const AcceptForm = ({ type, onLoadingChange }) => {
  const {
    configuration,
    dictionary,
    validation,
    token,
    values,
    reservationDetails,
    confirmationPageUrl,
    vehicle,
  } = window.modifyData
  const {
    modifiedPrices,
    modifiedValues,
    selectedVehicle,
    selectedExtras,
    isPrepaid,
    mbData,
    milesNBonusAccount,
    needsChanges,
    cancelationFee,
  } = useSelector((state) => state.booking)

  const paymentCondition = () => {
    return (
      type === 'modify' &&
      (modifiedPrices?.restToBePaidWithCard > 0 || needsChanges)
    )
  }

  const { firstName: firstNameMB, lastName: lastNameMB } =
    milesNBonusAccount || {}
  const sameUserAsMB = () => {
    // If not set or empty object, return true to show the login button
    if (!milesNBonusAccount || Object.keys(milesNBonusAccount).length === 0) {
      return true
    }

    return (
      firstNameMB?.toLowerCase() ===
        lib.maskStringLowerCase(values?.firstName) &&
      lastNameMB?.toLowerCase() === lib.maskStringLowerCase(values?.lastName)
    )
  }

  const milesNBonusCondition =
    paymentCondition() &&
    showMilesNBonus({
      selectedVehicle,
      milesNBonusAccount,
      isPrepaid,
    }) &&
    sameUserAsMB()

  const [isLoading, setIsLoading] = useState(false)
  const [showPayment, setShowPayment] = useState(true)
  const [showMilesNCash, setShowMilesNCash] = useState(false)
  const [selectedMiles, setSelectedMiles] = useState(0)
  const [formDataForOTP, setFormDataForOTP] = useState(null)
  const [openMilesNCashOTPModal, setOpenMilesNCashOTPModal] = useState(false)
  const [remoteUrl, setRemoteUrl] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [alertMessages, setAlertMessages] = useState([])

  const { estimatedTotal } = isPrepaid
    ? vehicle?.car?.prepaidPrice
    : vehicle?.car?.returnPrice

  const { initialRedeemedMiles } = values || {}

  useEffect(() => {
    setSelectedMiles(mbData.redeemAmount || 0)
  }, [mbData])

  const internationalEngine = !selectedVehicle?.isGREngine

  const schema = z.object({
    // terms
    terms: z.boolean().refine((val) => val === true, {
      message: dictionary.termsRequiredText,
    }),
  })
  const [currentSchema, setCurrentSchema] = useState(schema)

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitted },
    reset,
  } = useForm({
    resolver: zodResolver(currentSchema),
    mode: 'onSubmit',
    defaultValues: modifiedValues,
  })

  // Log validation errors whenever they change and form has been submitted
  useEffect(() => {
    if (isSubmitted && Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([fieldName, error]) => {
        // console.log(`Field: ${fieldName}`, error)
        if (error.type === 'invalid_type') {
          console
            .log
            // `Type error: Expected ${error._expected}, got ${typeof error._got}`
            ()
        }
      })
      console.groupEnd()
    }
  }, [errors, isSubmitted])

  const onSubmit = (data) => {
    if (type === 'cancel') {
      handleFormCancel(data)
      return
    }
    if (mbData.redeemEnabled) {
      handleRequestOTP(data)
    } else {
      handleFormSubmit(data)
    }
  }

  const handleRequestOTP = (data) => {
    // Store the form data for later use in OTP verification
    setFormDataForOTP(data)
    // Call requestOTP with the form data
    requestOTP(data)
  }

  const requestOTP = async (data) => {
    try {
      setTimeout(() => {
        if (isLoading) {
          return false
        }
      }, 0)

      // Use the stored form data if no data is provided (for resend OTP case)
      const formData = data || formDataForOTP
      if (!formData) {
        console.error('BookingStep3 - requestOTP - No form data available')
        return { hasError: true, errorMessages: ['No form data available'] }
      }
      setIsLoading(true)

      var wSize = 600
      const body = document.body.width

      if (body < 600) wSize = 600
      if (body < 500) wSize = 390
      if (body < 300) wSize = 250

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobilePhone: values.phone,
        mobileDialingCode: values.countryCode,
        landPhone: values.phone,
        landDialingCode: values.countryCode,
        subscribe: values.sms,
        marketing: values.marketing,
        flightNumber: values.flightNumber,
        useMilesRedemption: true,
        redeemedMiles: mbData.redeemAmount,
        referenceNumber: referenceNumber,

        // Navigator data
        navigatorLanguage: navigator.language,
        navigatorColors: screen.colorDepth,
        navigatorHeight: screen.height,
        navigatorWidth: screen.width,
        navigatorTimezone: new Date().getTimezoneOffset(),
        isJavaEnabled: navigator.javaEnabled(),
        challengeWindowSize: wSize,
        token: token,
      }

      if (shouldShowPayment) {
        appendCreditCardData(payload, formData)
      } else {
        appendNoCardData(payload, formData)
      }

      const response = await postData(
        configuration.endpointModifyConfirm,
        payload
      )

      const { HasError } = response

      if (HasError) {
        handleError(response.Errors)
        return response
      }

      setOpenMilesNCashOTPModal(true)
      handleSuccess()
      return response
    } catch (err) {
      console.error(err)
      handleError([err])
    }
  }

  const verifyOTP = async (otpCode) => {
    try {
      setTimeout(() => {
        if (isLoading) {
          return false
        }
      }, 0)

      // Use the stored form data instead of getting it from the form
      if (!formDataForOTP) {
        console.error('BookingStep3 - verifyOTP - No form data available')
        return false
      }

      setIsLoading(true)

      var wSize = 600
      const body = document.body.width

      if (body < 600) wSize = 600
      if (body < 500) wSize = 390
      if (body < 300) wSize = 250

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobilePhone: values.phone,
        mobileDialingCode: values.countryCode,
        landPhone: values.phone,
        landDialingCode: values.countryCode,
        subscribe: values.sms,
        marketing: values.marketing,
        flightNumber: values.flightNumber,
        useMilesRedemption: true,
        redeemedMiles: mbData.redeemAmount,
        otpCode: otpCode,
        referenceNumber: referenceNumber,

        // Navigator data
        navigatorLanguage: navigator.language,
        navigatorColors: screen.colorDepth,
        navigatorHeight: screen.height,
        navigatorWidth: screen.width,
        navigatorTimezone: new Date().getTimezoneOffset(),
        isJavaEnabled: navigator.javaEnabled(),
        challengeWindowSize: wSize,
        token: token,
      }

      if (shouldShowPayment) {
        appendCreditCardData(payload, formDataForOTP)
      } else {
        appendNoCardData(payload, formDataForOTP)
      }

      const response = await postData(
        configuration.endpointConfirmMBRedemption,
        payload
      )

      const { HasError, Url, RemoteUrl } = response

      if (HasError) {
        return response
      }

      setOpenMilesNCashOTPModal(false)

      if (Url) {
        window.location.assign(Url)
      }
      if (RemoteUrl) {
        lib.scrollToTop()
        setRemoteUrl(RemoteUrl)
        paymentShowIframe(RemoteUrl)
      }

      handleSuccess()
      return response
    } catch (err) {
      console.error(err)
      handleError([err])
    }
  }

  // FIX THIS
  const handleFormCancel = async (formData) => {
    try {
      var wSize = 600
      const body = document.body.width

      if (body < 600) wSize = 600
      if (body < 500) wSize = 390
      if (body < 300) wSize = 250

      const payload = {
        referenceNumber: reservationDetails.referenceNumber,
        lastName: reservationDetails.lastName,

        // Navigator data
        navigatorLanguage: navigator.language,
        navigatorColors: screen.colorDepth,
        navigatorHeight: screen.height,
        navigatorWidth: screen.width,
        navigatorTimezone: new Date().getTimezoneOffset(),
        isJavaEnabled: navigator.javaEnabled(),
        challengeWindowSize: wSize,
        token: token,
      }

      if (shouldShowPayment) {
        appendCreditCardData(payload, formData)
      }

      setIsLoading(true)

      const response = await postData(
        configuration.endpointCancelConfirm,
        payload
      )

      const { hasError, errorMessages, data } = response

      if (hasError) {
        handleError(errorMessages)
        return
      }

      if (data?.remoteUrl) {
        lib.scrollToTop()
        setRemoteUrl(data?.remoteUrl)
        paymentShowIframe(data?.remoteUrl)
      }

      if (data?.url) {
        window.location.assign(data?.url)
      }

      handleSuccess()
    } catch (err) {
      console.error(err)
      handleError([err.message])
    }
  }

  const handleFormSubmit = async (data) => {
    try {
      var wSize = 600
      const body = document.body.width

      if (body < 600) wSize = 600
      if (body < 500) wSize = 390
      if (body < 300) wSize = 250

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        flightNumber: values.flightNumber,
        mobilePhone: values.phone,
        mobileDialingCode: values.countryCode,
        landPhone: values.phone,
        landDialingCode: values.countryCode,
        subscribe: values.sms,
        marketing: values.marketing,
        useMilesRedemption: mbData?.redeemEnabled,
        redeemedMiles: mbData?.redeemEnabled ? mbData?.redeemAmount : 0,

        // Navigator data
        navigatorLanguage: navigator.language,
        navigatorColors: screen.colorDepth,
        navigatorHeight: screen.height,
        navigatorWidth: screen.width,
        navigatorTimezone: new Date().getTimezoneOffset(),
        isJavaEnabled: navigator.javaEnabled(),
        challengeWindowSize: wSize,
        token: token,
      }

      if (shouldShowPayment) {
        appendCreditCardData(payload, data)
      } else {
        appendNoCardData(payload, data)
      }

      setIsLoading(true)

      const response = await postData(
        configuration.endpointModifyConfirm,
        payload
      )

      const { HasError, Errors, Url, RemoteUrl, ReferenceNumber } = response

      if (HasError) {
        handleError(Errors)
        return response
      }

      if (mbData?.redeemEnabled) {
        setReferenceNumber(ReferenceNumber)
        setOpenMilesNCashOTPModal(true)
      } else {
        if (Url) {
          window.location.assign(Url)
        }
        if (RemoteUrl) {
          lib.scrollToTop()
          setRemoteUrl(RemoteUrl)
          paymentShowIframe(RemoteUrl)
        }
      }
      handleSuccess()
    } catch (err) {
      console.error(err)
      handleError([err.message])
    }
  }

  const handleSuccess = () => {
    setAlertMessages([])
    setIsLoading(false)
  }

  const handleError = (messages) => {
    if (messages === null || messages === undefined) {
      setAlertMessages([dictionary.defaultErrorMessage])
    } else if (typeof messages === 'string') {
      setAlertMessages([messages])
    } else {
      setAlertMessages(messages)
    }
    setIsLoading(false)
    setTimeout(() => {
      lib.scrollToError(110)
    }, 0)
  }

  const handleShowPayment = (value) => {
    // Only set payment visibility if there's remaining cost AND value is true
    setShowPayment(value && mbData?.hasRemainingCost)
  }
  useEffect(() => {
    if (mbData) {
      handleShowPayment(true)
    }
  }, [mbData])

  useEffect(() => {
    let updatedSchema = schema

    if (shouldShowPayment) {
      updatedSchema = updatedSchema.and(paymentSchema(dictionary))
    }

    setCurrentSchema(updatedSchema)
    reset({}, { keepValues: true })
  }, [showPayment, reset])

  const dispatch = useDispatch()

  const shouldShowPayment =
    (type === 'cancel' && cancelationFee > 0) ||
    (showPayment &&
      (isPrepaid ||
        internationalEngine ||
        selectedVehicle?.car?.requireCreditCard ||
        (mbData?.redeemEnabled && mbData?.hasRemainingCost)) &&
      paymentCondition())

  // Fixed implementation that doesn't use hooks directly
  const calculateHasRemainingCost = () => {
    if (!selectedVehicle || !selectedVehicle.car) {
      return true
    }

    const { prepaidPrice, returnPrice } = selectedVehicle.car

    const getRatePrice = () => {
      if (isPrepaid) {
        return prepaidPrice
      }
      return returnPrice
    }

    const totalPriceToMiles = () => {
      const conversionRate = milesNBonusAccount?.rate || 300
      return Math.round(getRatePrice().estimatedTotal * conversionRate)
    }

    const totalMiles = totalPriceToMiles()
    // Only consider miles if redemption is enabled
    const effectiveMiles = mbData?.redeemEnabled
      ? selectedMiles || mbData?.redeemAmount || 0
      : 0
    const hasRemainingCost = totalMiles > effectiveMiles

    return hasRemainingCost
  }

  const isEligibleToRedeem = () => {
    const remainingMiles =
      milesNBonusAccount?.remainingMiles > milesNBonusAccount?.minimumMiles
    const isEligible =
      milesNBonusAccount?.eligibilityStatus ===
      constants?.booking?.milesNBonus?.eligible
    const eligibleSelectedBrand =
      selectedVehicle.brandName.toLowerCase() ===
      constants.booking.milesNBonus.eligibleBrand.toLowerCase()

    return (
      !!remainingMiles && !!isEligible && isPrepaid && eligibleSelectedBrand
    )
  }

  const showBillingNonPrepaid = () => {
    if (internationalEngine) {
      // isGREngine is false
      handleShowPayment(true)
      setShowMilesNCash(false)
    } else if (selectedVehicle.car.requireCreditCard) {
      // Domestic engine (isGREngine is true) and requires credit card
      handleShowPayment(true)
      setShowMilesNCash(isEligibleToRedeem()) // Show miles if > 1500
    } else {
      // Domestic engine and doesn't require credit card
      handleShowPayment(false)
      setShowMilesNCash(false)
    }
  }

  useEffect(() => {
    if (isPrepaid) {
      handleShowPayment(true)
      if (!internationalEngine) {
        setShowMilesNCash(isEligibleToRedeem()) // Show miles if > 1500
      } else {
        setShowMilesNCash(false)
      }
    } else {
      showBillingNonPrepaid()
    }
  }, [])

  const handleCloseOTPModal = () => {
    setOpenMilesNCashOTPModal(false)
    setIsLoading(false)
  }

  const paymentShowIframe = (url) => {
    console.log('paymentShowIframe')
    setTimeout(() => {
      document.getElementById('payment-iframe-frame').style.display = 'block'
      document.getElementById('payment-iframe-frame').src = url
    }, 0)

    setTimeout(() => {
      lib.scrollToElem($('main'), 100)
      setIsLoading(false)
    }, 100)
  }

  const startNotifyTimer = (e) => {
    console.log('startNotifyTimer', e)

    setIsLoading(true)

    const { checkUrl, iframeUrl } = e.detail

    let start = 0
    const timer = window.setInterval(async () => {
      console.log('interval', start)

      if (start > 10) {
        console.log('start larger than 10')
        window.clearInterval(timer)
        document.getElementById('payment-iframe-frame').src = iframeUrl
        return
      }

      try {
        const response = await getData(checkUrl)

        if (response) {
          const { success, notified } = response

          if (success && notified) {
            console.log('success && notified')
            window.clearInterval(timer)
            document.getElementById('payment-iframe-frame').src = iframeUrl
            setTimeout(() => {
              setIsLoading(false)
            }, 1000)
          }
        }
      } catch (err) {
        console.error(err)
        handleError([err.message])
      }

      start += 1
    }, 1000)
  }

  const paymentRemoveIframe = (e) => {
    console.log('paymentRemoveIframe', e)

    setIsLoading(true)

    document.getElementById('payment-iframe-frame').style.display = 'none'

    const { errors, url } = e.detail
    if (url) {
      window.location.assign(url)
      return false
    }

    setRemoteUrl('')
    setIsLoading(false)
    setAlertMessages([dictionary.defaultErrorMessage])
    // if errors array is empty or
    // has just one item with empty string
    if (errors.length && lib.hasValue(errors[0])) {
      setAlertMessages(errors)
    }

    // lib.scrollToElem(document.querySelector(this.refs.strip), 100)
    lib.scrollToTop()
  }

  const iframeProcessCompleted = () => {
    setIsLoading(false)
  }

  const appendNoCardData = (payload, data) => {
    Object.assign(payload, {
      mobilePhone: values.mobilePhone,
      mobileDialingCode: values.mobileDialingCode,
      landPhone: values.landPhone,
      landDialingCode: values.landDialingCode,
    })
  }

  const appendCreditCardData = (payload, data) => {
    Object.assign(payload, {
      mobilePhone: values.mobilePhone,
      mobileDialingCode: values.mobileDialingCode,
      landPhone: values.landPhone,
      landDialingCode: values.landDialingCode,
      addressStreet: data.address,
      addressNumber: data.streetNumber,
      address2: data.addressAlt,
      postalCode: data.postalCode,
      city: data.city,
      country: data.country?.text,
      region: data.region?.text,
      addressLine: data.addressLine,
      additionalAddressLine: data.additionalAddressLine,
      cardNumber: data.cardNumber,
      cardHolder: data.cardHolder,
      cardExpirationMonth: data.expiryMonth,
      cardExpirationYear: data.expiryYear,
      cardSecureCode: data.cvv,
    })
  }

  useEffect(() => {
    const handleStartNotifyTimer = (e) => startNotifyTimer(e)
    const handlePaymentRemoveIframe = (e) => paymentRemoveIframe(e)
    const handleIframeProcessCompleted = (e) => iframeProcessCompleted(e)

    document.addEventListener('startNotifyTimer', handleStartNotifyTimer)
    document.addEventListener('paymentRemoveIframe', handlePaymentRemoveIframe)
    // prettier-ignore
    document.addEventListener('iframeProcessCompleted', handleIframeProcessCompleted)

    // prettier-ignore
    return () => {
        document.removeEventListener('startNotifyTimer', handleStartNotifyTimer)
        document.removeEventListener('paymentRemoveIframe', handlePaymentRemoveIframe)
        document.removeEventListener('iframeProcessCompleted', handleIframeProcessCompleted)
      }
  }, [])

  const iframeStyles = {
    border: 'none',
    width: '100%',
    height: '450px',
    overflow: 'auto',
  }

  useEffect(() => {
    const handleMBLogin = (event) => {
      const isLoggedIn = event.detail.isLoggedIn
      if (!isLoggedIn) {
        dispatch(resetMilesNBonusAccount())
        dispatch(resetMBData())
      } else {
        dispatch(setMilesNBonusAccount(event.detail.data))
      }
      fetchData()
    }
    window.addEventListener('loggedIn', handleMBLogin)

    return () => {
      window.removeEventListener('loggedIn', handleMBLogin)
    }
  }, [])

  const fetchData = () => {
    setIsLoading(true)
    const body = {
      amenities: selectedExtras.map(({ code, quantity }) => ({
        Code: code,
        Quantity: quantity,
      })),
      pickUpLocation: {
        code: modifiedValues.pickUpLocation.value,
        country: modifiedValues.pickUpLocation.country,
      },
      dropOffLocation: {
        code: modifiedValues.dropOffLocation.value,
        country: modifiedValues.dropOffLocation.country,
      },
      pickUpDate: modifiedValues.pickUpDate,
      pickUpTime: modifiedValues.pickUpTime,
      dropOffDate: modifiedValues.dropOffDate,
      dropOffTime: modifiedValues.dropOffTime,
      firstName: modifiedValues.firstName,
      lastName: modifiedValues.lastName,
      email: modifiedValues.email,
      countryCode: modifiedValues.countryCode,
      phone: modifiedValues.phone,
      airline: modifiedValues.airline,
      flightNumber: modifiedValues.flightNumber,
    }
    postData(configuration.endpointModify, body)
      .then((response) => {
        if (response.hasError) {
          handleError(response.errorMessages)
          return
        }

        dispatch(setSelectedVehicle(response.data.vehicle))
        handleModifiedPrices(response)
        handleSuccess()
      })
      .catch((err) => {
        console.error(err)
        handleError([err.message])
      })
  }

  const handleModifiedPrices = (response) => {
    const { needsChanges, priceDifference, hasAddressData } = response.data
    dispatch(setNeedsChanges(needsChanges))
    const modifiedPrices = {
      initialPaymentWithCard: estimatedTotal - (initialRedeemedMiles || 0),
      initialPaymentWithMiles: initialRedeemedMiles || 0,
    }
    if (priceDifference > 0) {
      modifiedPrices.restToBePaidWithCard = priceDifference
    } else if (priceDifference < 0) {
      modifiedPrices.refund = -1 * priceDifference
    }
    dispatch(setModifiedPrices(modifiedPrices))
  }

  useEffect(() => {
    onLoadingChange(isLoading)
  }, [isLoading])

  return (
    <>
      {alertMessages.length > 0 && <Alert messages={alertMessages} />}

      <div
        className="payment-iframe-container"
        style={{ display: remoteUrl ? 'block' : 'none' }}>
        <iframe
          id="payment-iframe-frame"
          title="payment"
          style={iframeStyles}></iframe>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: !remoteUrl ? 'block' : 'none' }}>
        <ReservationFields
          control={control}
          setValue={setValue}
          watch={watch}
          errors={errors}
          disabled
        />
        <div className="s-booking-form__separator"></div>
        <DriverFields control={control} />
        <div className="s-booking-form__separator"></div>
        {milesNBonusCondition && (
          <>
            <MilesNCash
              control={control}
              errors={errors}
              setValue={setValue}
              isLoading={isLoading}
              triggerLoginModal={() =>
                window.dispatchEvent(new CustomEvent('openLoginModal'))
              }
              totalPrice={modifiedPrices?.restToBePaidWithCard}
            />
            <div className="s-booking-form__separator"></div>
          </>
        )}
        {shouldShowPayment && <Payment control={control} errors={errors} />}

        <div className="mt-40">
          <Terms
            endpoint={configuration.endpointTermsNConditions}
            dictionary={dictionary}
            control={control}
            termsOnly={false}
          />
        </div>

        <div className="s-modify-form__footer">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={isLoading}>
            {isLoading ? (
              <CircularProgress color="inherit" size={24} />
            ) : (
              dictionary.confirm
            )}
          </button>
        </div>

        <SummaryMobilePanel />
      </form>
      <MilesNCashOTPModal
        open={openMilesNCashOTPModal}
        handleClose={handleCloseOTPModal}
        selectedMiles={selectedMiles}
        hasRemainingCost={calculateHasRemainingCost()}
        verifyOTP={verifyOTP}
        requestOTP={requestOTP}
      />
    </>
  )
}

export default AcceptForm
